import { BodyChartPicker } from "@/components/app/body-chart-picker"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Textarea } from "@/components/ui/textarea"
import { defaultFieldValue } from "@/lib/care-plan-form-utils"
import { Plus, Trash2 } from "lucide-react"

function SmartGoalsEditor({ value, onChange, disabled }) {
  const v = value || { short: [], medium: [], long: [] }
  const horizons = [
    { key: "short", label: "Termen scurt" },
    { key: "medium", label: "Termen mediu" },
    { key: "long", label: "Termen lung" },
  ]
  return (
    <div className="space-y-3">
      {horizons.map(({ key, label }) => (
        <div key={key}>
          <Label className="text-xs">{label}</Label>
          <Textarea
            disabled={disabled}
            className="mt-1 text-sm min-h-[60px]"
            placeholder="Câte un obiectiv pe linie"
            value={(v[key] || []).join("\n")}
            onChange={(e) =>
              onChange({
                ...v,
                [key]: e.target.value.split("\n").map((s) => s.trim()).filter(Boolean),
              })
            }
          />
        </div>
      ))}
    </div>
  )
}

function RepeatableGroup({ field, value, onChange, disabled }) {
  const rows = Array.isArray(value) ? value : []
  const schema = field.group_schema || []
  const addRow = () => {
    const row = {}
    schema.forEach((col) => {
      row[col.id] = col.type === "number" ? null : ""
    })
    onChange([...rows, row])
  }
  const updateRow = (idx, colId, val) => {
    const next = rows.map((r, i) => (i === idx ? { ...r, [colId]: val } : r))
    onChange(next)
  }
  const removeRow = (idx) => onChange(rows.filter((_, i) => i !== idx))
  return (
    <div className="space-y-2">
      {rows.map((row, idx) => (
        <div key={idx} className="grid sm:grid-cols-4 gap-2 items-end border rounded-lg p-2 bg-muted/20">
          {schema.map((col) => (
            <div key={col.id}>
              <Label className="text-[10px]">{col.label}</Label>
              {col.type === "select" ? (
                <Select
                  disabled={disabled}
                  value={row[col.id] || ""}
                  onValueChange={(v) => updateRow(idx, col.id, v)}
                >
                  <SelectTrigger className="h-8 mt-0.5"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {(col.options || []).map((o) => (
                      <SelectItem key={o} value={o}>{o}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <Input
                  disabled={disabled}
                  type={col.type === "number" ? "number" : col.type === "date" ? "date" : "text"}
                  className="h-8 mt-0.5"
                  value={row[col.id] ?? ""}
                  onChange={(e) => updateRow(idx, col.id, e.target.value)}
                />
              )}
            </div>
          ))}
          {!disabled && (
            <Button type="button" size="icon" variant="ghost" className="h-8" onClick={() => removeRow(idx)}>
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          )}
        </div>
      ))}
      {!disabled && (
        <Button type="button" size="sm" variant="outline" className="rounded-full" onClick={addRow}>
          <Plus className="h-3.5 w-3.5 mr-1" /> Adaugă rând
        </Button>
      )}
    </div>
  )
}

function FieldControl({ field, value, onChange, disabled }) {
  const t = field.type
  if (t === "textarea") {
    return (
      <Textarea disabled={disabled} value={value || ""} onChange={(e) => onChange(e.target.value)} className="min-h-[80px]" />
    )
  }
  if (t === "checkbox") {
    return (
      <label className="flex items-center gap-2 text-sm">
        <input type="checkbox" disabled={disabled} checked={!!value} onChange={(e) => onChange(e.target.checked)} />
        Confirmat
      </label>
    )
  }
  if (t === "select") {
    return (
      <Select disabled={disabled} value={value || ""} onValueChange={onChange}>
        <SelectTrigger><SelectValue placeholder="Alege" /></SelectTrigger>
        <SelectContent>
          {(field.options || []).map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
        </SelectContent>
      </Select>
    )
  }
  if (t === "multiselect") {
    const selected = Array.isArray(value) ? value : []
    return (
      <div className="flex flex-wrap gap-2">
        {(field.options || []).map((o) => (
          <label key={o} className="text-xs flex items-center gap-1 border rounded-full px-2 py-1">
            <input
              type="checkbox"
              disabled={disabled}
              checked={selected.includes(o)}
              onChange={(e) => {
                if (e.target.checked) onChange([...selected, o])
                else onChange(selected.filter((x) => x !== o))
              }}
            />
            {o}
          </label>
        ))}
      </div>
    )
  }
  if (t === "smart_goals") return <SmartGoalsEditor value={value} onChange={onChange} disabled={disabled} />
  if (t === "scale_score") {
    const v = value || { scale: field.scale || "", score: "", notes: "" }
    return (
      <div className="grid sm:grid-cols-3 gap-2">
        <Input disabled={disabled} placeholder="Scor" value={v.score ?? ""} onChange={(e) => onChange({ ...v, score: e.target.value })} />
        <Input disabled={disabled} className="sm:col-span-2" placeholder="Notițe" value={v.notes || ""} onChange={(e) => onChange({ ...v, notes: e.target.value })} />
      </div>
    )
  }
  if (t === "repeatable_group") return <RepeatableGroup field={field} value={value} onChange={onChange} disabled={disabled} />
  if (t === "body_chart") {
    const chart = value || defaultFieldValue(field)
    return (
      <BodyChartPicker
        view={chart.view === "back" ? "back" : "front"}
        regions={chart.regions || []}
        onViewChange={(v) => onChange({ ...chart, view: v })}
        onToggleRegion={(rid) => {
          const cur = chart.regions || []
          const next = cur.includes(rid) ? cur.filter((x) => x !== rid) : [...cur, rid]
          onChange({ ...chart, regions: next })
        }}
        pressureNotes={chart.pressure_notes || ""}
        onPressureNotesChange={(txt) => onChange({ ...chart, pressure_notes: txt })}
      />
    )
  }
  if (t === "icd_diagnosis") {
    const v = value || { code: "", label: "" }
    return (
      <div className="grid sm:grid-cols-2 gap-2">
        <Input disabled={disabled} placeholder="Cod" value={v.code || ""} onChange={(e) => onChange({ ...v, code: e.target.value })} />
        <Input disabled={disabled} placeholder="Descriere" value={v.label || ""} onChange={(e) => onChange({ ...v, label: e.target.value })} />
      </div>
    )
  }
  if (t === "meal_plan_week") {
    return (
      <Textarea
        disabled={disabled}
        className="min-h-[100px] font-mono text-xs"
        value={JSON.stringify(value || defaultFieldValue(field), null, 2)}
        onChange={(e) => {
          try {
            onChange(JSON.parse(e.target.value))
          } catch { /* ignore */ }
        }}
      />
    )
  }
  if (t === "number") return <Input disabled={disabled} type="number" value={value ?? ""} onChange={(e) => onChange(e.target.value)} />
  if (t === "date") return <Input disabled={disabled} type="date" value={value || ""} onChange={(e) => onChange(e.target.value)} />
  return <Input disabled={disabled} value={value || ""} onChange={(e) => onChange(e.target.value)} />
}

export function CarePlanFormRenderer({ definition, fieldValues, onChange, disabled = false }) {
  const values = fieldValues || {}
  const setField = (fid, val) => onChange({ ...values, [fid]: val })

  return (
    <div className="space-y-6">
      {(definition?.sections || []).map((sec) => (
        <section key={sec.id} className="rounded-xl border border-border p-4 bg-card space-y-4">
          <h3 className="text-sm font-semibold">{sec.label}</h3>
          {(sec.fields || []).map((field) => {
            const fid = field.id
            if (!fid) return null
            const val = values[fid] !== undefined ? values[fid] : defaultFieldValue(field)
            return (
              <div key={fid} className="space-y-1.5">
                {field.type !== "checkbox" && (
                  <Label className="text-xs">
                    {field.label}
                    {field.required ? <span className="text-destructive ml-1">*</span> : null}
                  </Label>
                )}
                <FieldControl field={field} value={val} onChange={(v) => setField(fid, v)} disabled={disabled} />
              </div>
            )
          })}
        </section>
      ))}
    </div>
  )
}
